import { Layout, Col, Form, Input, Row, Radio } from 'antd';
import { BaseButton } from 'components/UI/BaseButton';
import Breadcrumb from 'components/UI/Breadcrumb';
import ScrollToTop from 'components/ScrollToTop';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  CREATE_STATUS,
  UPDATE_STATUS,
  navigationRoutes,
  STATUS,
  compareArrayObject,
} from 'utilities';
import {
  getTextSubPage,
  createTextSubPage,
  updateTextSubPage,
  updatePreviewTextSubPage,
  resetTextSubPageData,
} from 'redux/microSite/operation';
import {
  selectTextSubPage,
  selectCreateTextSubPage,
  selectUpdateTextSubPage,
} from 'redux/microSite/selector';
import { selectMenuInfo } from 'redux/administration/menu/selector';
import { BaseUpload } from 'components/UI/BaseUpload';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import Preview from './Preview';

const MicroTextSubPage = () => {
  const { Content } = Layout;
  const { TextArea } = Input;
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const history = useHistory();
  const typingTimeoutRef = useRef(null);

  const { tileId, subPageId } = useParams();

  const { textSubPage, textSubPageCreate, textSubPageUpdate, cmsMenu } =
    useSelector((state) => ({
      textSubPage: selectTextSubPage(state),
      textSubPageCreate: selectCreateTextSubPage(state),
      textSubPageUpdate: selectUpdateTextSubPage(state),
      cmsMenu: selectMenuInfo(state),
    }));

  const isEditable = useMemo(() => !!subPageId, [subPageId]);

  useEffect(() => {
    if (subPageId) {
      dispatch(getTextSubPage(subPageId));
    }
  }, [dispatch, tileId, subPageId]);

  const onFinish = useCallback(
    (values) => {
      values.status = values.status === STATUS.ACTIVE;
      if (isEditable && textSubPage) {
        const [comparedCategories] = compareArrayObject(
          textSubPage.categories,
          values.categories,
        );
        values.categories = comparedCategories;
        dispatch(updateTextSubPage(subPageId, values));
      } else {
        values.tileId = tileId;
        dispatch(createTextSubPage(values));
      }
    },
    [dispatch, tileId, subPageId, isEditable, textSubPage],
  );

  useEffect(() => {
    if (
      textSubPageCreate.status === CREATE_STATUS.SUCCESS ||
      textSubPageUpdate.status === UPDATE_STATUS.SUCCESS
    ) {
      history.push(`${navigationRoutes.MicroSingleStartPage}/${tileId}`);
    }
  }, [history, textSubPageCreate.status, textSubPageUpdate.status, tileId]);

  useEffect(() => {
    const formValues =
      isEditable && textSubPage
        ? {
            title: textSubPage.title,
            textHeadline: textSubPage.textHeadline,
            mediaHeadline: textSubPage.mediaHeadline,
            categories: textSubPage.categories,
            status: textSubPage.status ? STATUS.ACTIVE : STATUS.INACTIVE,
          }
        : {
            title: null,
            textHeadline: null,
            mediaHeadline: null,
            categories: [{}],
            status: STATUS.INACTIVE,
          };
    form.setFieldsValue(formValues);
    dispatch(updatePreviewTextSubPage(formValues));
  }, [dispatch, isEditable, textSubPage, form]);

  useEffect(() => {
    return () => {
      dispatch(resetTextSubPageData());
    };
  }, [dispatch]);

  const onUploadMediaHeadline = useCallback(
    (value) => {
      form.setFieldsValue({
        mediaHeadline: value,
      });
      dispatch(
        updatePreviewTextSubPage({
          mediaHeadline: value,
        }),
      );
    },
    [dispatch, form],
  );

  const onValuesChange = useCallback(
    (_, allValues) => {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        dispatch(updatePreviewTextSubPage(allValues));
      }, 500);
    },
    [dispatch],
  );

  const onCancel = useCallback(() => {
    history.push(`${navigationRoutes.MicroSingleStartPage}/${tileId}`);
  }, [history, tileId]);

  const breadCrumbTitle = useMemo(() => {
    if (!isEditable) return 'Text Sub Page';
    if (isEditable && textSubPage && textSubPage.title)
      return textSubPage.title;
    return '';
  }, [isEditable, textSubPage]);

  const startPageTitle = useMemo(() => {
    if (cmsMenu) {
      const allContents = cmsMenu.menu.find((item) => item.children);
      const startPage = allContents.children.find(
        (content) => content.id === Number(tileId),
      );
      return startPage ? startPage.title : '';
    }
    return '';
  }, [cmsMenu, tileId]);

  const breadCrumbItems = useMemo(
    () => [
      {
        title: 'Home',
        path: navigationRoutes.Home,
      },
      {
        title: 'Administration',
        path: navigationRoutes.UserManagement,
      },
      {
        title: 'Content Management',
        path: navigationRoutes.MapoContent,
      },
      {
        title: startPageTitle,
        path: `${navigationRoutes.MicroSingleStartPage}/${tileId}`,
      },
      {
        title: breadCrumbTitle,
      },
    ],
    [tileId, startPageTitle, breadCrumbTitle],
  );

  return (
    <Content className="micro-text-sub-page">
      <Breadcrumb items={breadCrumbItems} />
      <div className="text-sub-page_banner mb-6" />
      <div className="middle-content">
        <Form
          form={form}
          onValuesChange={onValuesChange}
          className="text-sub-page_form"
          layout="vertical"
          onFinish={onFinish}>
          <Row span={24} gutter={24}>
            <Col xs={24} md={16} lg={16}>
              <h3 className="form-title">SITE INFORMATION</h3>
              <Form.Item
                className={isEditable ? 'title' : ''}
                label={!isEditable ? 'TITLE' : ''}
                name="title"
                rules={[{ required: true, message: 'Please input title!' }]}>
                <Input
                  placeholder="Title"
                  className={!isEditable ? 'headline-title' : 'title-input'}
                />
              </Form.Item>

              <Form.Item label="TEXT HEADLINE" name="textHeadline">
                <TextArea
                  className="headline-text"
                  placeholder="Text headline"
                  autoSize
                />
              </Form.Item>

              <Form.Item
                className="media-headline"
                name="mediaHeadline"
                label="MEDIA HEADLINE">
                <BaseUpload
                  initialValue={isEditable ? textSubPage?.mediaHeadline : null}
                  onUploadChange={onUploadMediaHeadline}
                />
              </Form.Item>

              <Form.List name="categories">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <div className="sub-headline" key={key}>
                        <Form.Item
                          {...restField}
                          label="SUBHEADLINE TITLE"
                          name={[name, 'title']}
                          key={[key, 'title']}
                          rules={[
                            {
                              required: true,
                              message: 'Please input title!',
                            },
                          ]}>
                          <Input
                            placeholder="Subheadline title"
                            className="headline-title"
                          />
                        </Form.Item>

                        <Form.Item
                          {...restField}
                          label="CONTENT TEXT"
                          name={[name, 'text']}
                          key={[key, 'text']}>
                          <TextArea
                            className="headline-text"
                            placeholder="Content text"
                            autoSize
                          />
                        </Form.Item>

                        <MinusCircleOutlined onClick={() => remove(name)} />
                      </div>
                    ))}

                    <div className="d-flex justify-space-between">
                      <BaseButton
                        className="bold-text"
                        htmlType="button"
                        onClick={onCancel}>
                        CANCEL
                      </BaseButton>
                      <BaseButton
                        className="add-category-button"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                      />
                      <BaseButton
                        loading={
                          isEditable
                            ? textSubPageUpdate.isLoading
                            : textSubPageCreate.isLoading
                        }
                        className="base-yellow-btn"
                        type="primary"
                        htmlType="submit">
                        SAVE CONTENT
                      </BaseButton>
                    </div>
                  </>
                )}
              </Form.List>
            </Col>
            <Col xs={24} md={8} lg={8}>
              <Preview startPageTitle={startPageTitle} />
              <Form.Item
                className="text-sub-page_status"
                name="status"
                label="STATUS">
                <Radio.Group className="group-status">
                  <Radio.Button className="group-status-item" value="active">
                    <span className="label-radio">ACTIVE</span>
                  </Radio.Button>
                  <Radio.Button
                    className="group-status-item ml-4"
                    value="inactive">
                    <span className="label-radio">INACTIVE</span>
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </div>
      <ScrollToTop />
    </Content>
  );
};

export default MicroTextSubPage;
